import { Navigate } from 'react-router-dom';
import { Alert, AlertDescription, AlertTitle, Button } from '@design-system';
import { useIsConsumerSession } from '@/hooks/use-app-mode';
import { ConsumerFlowLayout } from './components/consumer-flow-layout';
import { ConsumerLoadingState } from './components/consumer-loading-state';
import { useConsumerClient } from './hooks/use-consumer-client';
import { useConsumerPlan } from './hooks/use-consumer-plan';
import { useLegacyFlowCompletionStamp } from './hooks/use-legacy-flow-completion-stamp';
import { usePolicyReviewInProgress } from './hooks/use-policy-review';
import { useRememberStep } from './hooks/use-remember-step';
import { useSelfFundRouteGuard } from './hooks/use-self-fund-route-guard';
import { useStepQuotes } from './hooks/use-step-quotes';
import { hasReachedFlowCompletion } from './lib/flow-completion';
import { currentConsumerStep, isApplicationStep, isConsumerStep, type ConsumerStepId } from './lib/steps';
import { consumerEntryPath, consumerStepPath } from './lib/paths';
import { CONSUMER_STEP_COMPONENTS } from './steps';

interface ConsumerFlowPageProps {
  clientId: string;
  step: string;
}

function LoadError({ title, onRetry }: { title: string; onRetry: () => void }) {
  return (
    <Alert variant="destructive">
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="flex flex-col items-start gap-2">
        Please try again in a moment.
        <Button variant="outline" size="sm" onClick={onRetry}>
          Try again
        </Button>
      </AlertDescription>
    </Alert>
  );
}

interface ConsumerStepContentProps {
  clientId: string;
  step: ConsumerStepId;
  isLoading: boolean;
  client: { isError: boolean; retry: () => void };
  plan: { isError: boolean; retry: () => void };
  quotes: { hasFailed: boolean; retry: () => void };
}

function ConsumerStepContent({ clientId, step, isLoading, client, plan, quotes }: ConsumerStepContentProps) {
  const Step = CONSUMER_STEP_COMPONENTS[step];

  if (client.isError) return <LoadError title="We couldn’t load your plan" onRetry={client.retry} />;
  if (plan.isError) return <LoadError title="We couldn’t load your plan" onRetry={plan.retry} />;
  if (quotes.hasFailed) return <LoadError title="We couldn’t load your insurance options" onRetry={quotes.retry} />;
  if (isLoading) return <ConsumerLoadingState clientId={clientId} />;
  return <Step key={step} clientId={clientId} />;
}

// Screens that start a new policy review; while one is with our team they lead to its status.
const NEW_REVIEW_STEPS: readonly ConsumerStepId[] = ['existing-policy', 'review-upload', 'review-checkout'];

// Where this step sends the consumer instead of rendering, if anywhere. Paid self-funding steps go to
// the paywall first; Your Plan waits for an intentional finish (checked once the plan has loaded, so a
// refresh never bounces). After applying, every other screen stays open (the plan stays live); only the
// application screens for the option already applied for lead to Your Plan, which shows that
// application as sent. Applying for another option is a new application.
function stepRedirect({
  clientId,
  step,
  accessRedirect,
  awaitsFlowCompletion,
  hasAppliedForChosenOption,
  reviewInProgress,
  isChoiceMissing,
}: {
  clientId: string;
  step: ConsumerStepId;
  accessRedirect: string | null;
  awaitsFlowCompletion: boolean;
  hasAppliedForChosenOption: boolean;
  reviewInProgress: boolean;
  isChoiceMissing: boolean;
}): string | null {
  if (accessRedirect) return accessRedirect;
  if (awaitsFlowCompletion) return consumerEntryPath(clientId);
  if (hasAppliedForChosenOption && isApplicationStep(step)) return consumerStepPath(clientId, 'your-plan');
  if (reviewInProgress && NEW_REVIEW_STEPS.includes(step)) return consumerStepPath(clientId, 'review-status');
  if (isChoiceMissing) return consumerStepPath(clientId, 'options');
  return null;
}

function ConsumerStep({ clientId, step }: { clientId: string; step: ConsumerStepId }) {
  const { isLoading: isClientLoading, isError: isClientError, retry: retryClient } = useConsumerClient(clientId);
  const { plan, isLoading: isPlanLoading, isError, refetch, applications } = useConsumerPlan(clientId);
  const isConsumerSession = useIsConsumerSession();
  const { isStamping } = useLegacyFlowCompletionStamp(clientId);
  const quotes = useStepQuotes(clientId, step);
  const reviewInProgress = usePolicyReviewInProgress(clientId);
  const access = useSelfFundRouteGuard(clientId, { kind: 'consumer-step', step });

  const redirectTo = stepRedirect({
    clientId,
    step,
    accessRedirect: access.kind === 'redirect' ? access.to : null,
    awaitsFlowCompletion:
      step === 'your-plan' &&
      isConsumerSession &&
      !isPlanLoading &&
      !isError &&
      !hasReachedFlowCompletion(plan, applications),
    hasAppliedForChosenOption:
      !!plan?.selectedQuoteKey && applications.some(application => application.coverage.quoteKey === plan.selectedQuoteKey),
    reviewInProgress: !!reviewInProgress,
    isChoiceMissing: !isPlanLoading && !isError && quotes.isChoiceMissing,
  });
  useRememberStep(clientId, step, { enabled: redirectTo === null });

  if (redirectTo) return <Navigate to={redirectTo} replace />;

  return (
    <ConsumerFlowLayout clientId={clientId} step={step}>
      <ConsumerStepContent
        clientId={clientId}
        step={step}
        isLoading={isClientLoading || isPlanLoading || quotes.isWaiting || isStamping}
        client={{ isError: isClientError, retry: retryClient }}
        plan={{ isError, retry: () => refetch() }}
        quotes={{ hasFailed: quotes.hasFailed, retry: () => quotes.retry() }}
      />
    </ConsumerFlowLayout>
  );
}

export function ConsumerFlowPage({ clientId, step }: ConsumerFlowPageProps) {
  if (isConsumerStep(step)) return <ConsumerStep clientId={clientId} step={step} />;
  const replacement = currentConsumerStep(step);
  return <Navigate to={replacement ? consumerStepPath(clientId, replacement) : consumerEntryPath(clientId)} replace />;
}
